import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import useStore from '../store/useStore'
import { exportAsJSON, exportAsPDF, calculateProgress } from '../services/roadmapUtils'
import { deleteRoadmapMetadata, publishRoadmap, unpublishRoadmap, saveRoadmapToSupabase, supabase } from '../services/supabase'
import ChatWidget from '../components/ChatWidget'
import ResourceCard from '../components/ResourceCard'

const RoadmapViewer = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { roadmaps, currentRoadmap, setCurrentRoadmap, removeRoadmap, user } = useStore()
  const [completedTasks, setCompletedTasks] = useState([])
  const [expandedWeeks, setExpandedWeeks] = useState([0])
  const [isExporting, setIsExporting] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showPublishModal, setShowPublishModal] = useState(false)
  const [showMenu, setShowMenu] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [saveStatus, setSaveStatus] = useState(null)
  const [publishData, setPublishData] = useState({
    author_name: user?.user_metadata?.full_name || user?.email?.split('@')[0] || '',
    description: '',
    tags: []
  })

  useEffect(() => {
    const loadRoadmap = async () => {
      // First try to load from store
      const roadmap = roadmaps.find(r => r.id === id)
      if (roadmap) {
        // If roadmap_data exists (from database), use it
        if (roadmap.roadmap_data) {
          const parsedRoadmap = typeof roadmap.roadmap_data === 'string' 
            ? JSON.parse(roadmap.roadmap_data)
            : roadmap.roadmap_data
          
          // Merge database metadata with roadmap data
          setCurrentRoadmap({
            ...roadmap,
            ...parsedRoadmap,
            id: roadmap.id // Preserve the ID
          })
        } else {
          setCurrentRoadmap(roadmap)
        }
      } else {
        // Fetch from Supabase if not in store
        console.log('Roadmap not in store, fetching from Supabase...')
        if (supabase && user) {
          try {
            const { data, error } = await supabase
              .from('roadmaps')
              .select('*')
              .eq('id', id)
              .eq('user_id', user.id)
              .single()
            
            if (error) {
              console.error('Error fetching roadmap:', error)
              return
            }
            
            if (data) {
              // Parse roadmap_data if it exists
              if (data.roadmap_data) {
                const parsedRoadmap = typeof data.roadmap_data === 'string'
                  ? JSON.parse(data.roadmap_data)
                  : data.roadmap_data
                
                setCurrentRoadmap({
                  ...data,
                  ...parsedRoadmap,
                  id: data.id
                })
              } else {
                setCurrentRoadmap(data)
              }
            }
          } catch (error) {
            console.error('Error loading roadmap:', error)
          }
        }
      }
    }
    
    loadRoadmap()
  }, [id, roadmaps, setCurrentRoadmap, user])

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showMenu && !event.target.closest('.menu-container')) {
        setShowMenu(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showMenu])

  const toggleTaskComplete = (taskId) => {
    setCompletedTasks(prev =>
      prev.includes(taskId)
        ? prev.filter(id => id !== taskId)
        : [...prev, taskId]
    )
  }

  const toggleWeek = (weekNum) => {
    setExpandedWeeks(prev =>
      prev.includes(weekNum)
        ? prev.filter(w => w !== weekNum)
        : [...prev, weekNum]
    )
  }
  
  const isWeekExpanded = (weekNum) => {
    return expandedWeeks.includes(weekNum)
  }

  const handleExportJSON = () => {
    if (currentRoadmap) {
      exportAsJSON(currentRoadmap)
      setShowMenu(false)
    }
  }

  const handleExportPDF = async () => {
    if (!currentRoadmap) return
    
    setIsExporting(true)
    setShowMenu(false)
    const { error } = await exportAsPDF(currentRoadmap)
    
    if (error) {
      alert('Failed to export PDF: ' + error)
    }
    
    setIsExporting(false)
  }

  const handleDelete = async () => {
    if (!currentRoadmap) return
    
    const { error } = await deleteRoadmapMetadata(currentRoadmap.id)
    if (!error) {
      removeRoadmap(currentRoadmap.id)
      navigate('/dashboard')
    } else {
      alert('Failed to delete roadmap: ' + error.message)
    }
  }

  const handlePublish = async () => {
    if (!currentRoadmap) return
    
    const { error } = await publishRoadmap(currentRoadmap.id, publishData)
    if (!error) {
      setShowPublishModal(false)
      alert('Roadmap published successfully! 🎉')
    } else {
      alert('Failed to publish roadmap: ' + error.message)
    }
  }

  const handleUnpublish = async () => {
    if (!currentRoadmap) return
    
    const { error } = await unpublishRoadmap(currentRoadmap.id)
    if (!error) {
      alert('Roadmap unpublished successfully!')
    } else {
      alert('Failed to unpublish roadmap: ' + error.message)
    }
  }

  const handleSaveToSupabase = async () => {
    if (!currentRoadmap || !user) return
    
    setIsSaving(true)
    setSaveStatus(null)
    
    try {
      const { data, error } = await saveRoadmapToSupabase(currentRoadmap, user.id)
      
      if (error) {
        if (error.message.includes('Storage limit reached')) {
          setSaveStatus({ type: 'error', message: error.message })
        } else {
          setSaveStatus({ type: 'error', message: 'Failed to save roadmap' })
        }
      } else {
        setSaveStatus({ type: 'success', message: '✓ Roadmap saved successfully!' })
        // Update the roadmap in store with the saved data
        if (data) {
          setCurrentRoadmap({ ...currentRoadmap, ...data })
        }
      }
    } catch (error) {
      console.error('Error saving roadmap:', error)
      setSaveStatus({ type: 'error', message: 'Failed to save roadmap' })
    } finally {
      setIsSaving(false)
      setTimeout(() => setSaveStatus(null), 5000)
    }
  }

  if (!currentRoadmap) {
    return (
      <div className="min-h-screen gradient-bg flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-violet-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading roadmap...</p>
        </div>
      </div>
    )
  }

  // Check if roadmap has weeks data
  if (!currentRoadmap.weeks || !Array.isArray(currentRoadmap.weeks)) {
    return (
      <div className="min-h-screen gradient-bg flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-8">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h3 className="text-2xl font-black text-gray-900 mb-2">Invalid Roadmap Format</h3>
          <p className="text-gray-600 mb-6">
            This roadmap doesn&apos;t have the correct structure. It may be from an older version.
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-6 py-3 bg-gradient-to-r from-violet-600 to-cyan-600 text-white font-bold rounded-xl hover:shadow-lg transition-all"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    )
  }

  const progress = calculateProgress(currentRoadmap, completedTasks)

  return (
    <div className="gradient-bg pb-20">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              <span className="font-medium">Back to Dashboard</span>
            </button>

            <div className="flex items-center gap-2">
              {/* Save Button */}
              <button
                onClick={handleSaveToSupabase}
                disabled={isSaving}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-violet-600 to-cyan-600 text-white rounded-lg hover:from-violet-700 hover:to-cyan-700 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                </svg>
                <span className="font-medium">{isSaving ? 'Saving...' : 'Save Roadmap'}</span>
              </button>
              
              <div className="relative menu-container">
                <button 
                  onClick={() => setShowMenu(!showMenu)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                  </svg>
                </button>
                
                {showMenu && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-2xl border border-gray-200 z-50 animate-in fade-in slide-in-from-top-2 duration-200">
                  <button
                    onClick={handleExportJSON}
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center gap-3 rounded-t-xl transition-colors"
                  >
                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                    <span className="font-medium">Export as JSON</span>
                  </button>
                  <button
                    onClick={handleExportPDF}
                    disabled={isExporting}
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center gap-3 transition-colors disabled:opacity-50"
                  >
                    <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                    <span className="font-medium">{isExporting ? 'Exporting...' : 'Export as PDF'}</span>
                  </button>
                  
                  <div className="border-t border-gray-200 my-1"></div>
                  
                  <button
                    onClick={() => {
                      setShowPublishModal(true)
                      setShowMenu(false)
                    }}
                    className="w-full text-left px-4 py-3 hover:bg-violet-50 flex items-center gap-3 transition-colors"
                  >
                    <svg className="w-5 h-5 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                    </svg>
                    <span className="font-medium">Publish to Exchange</span>
                  </button>
                  
                  <div className="border-t border-gray-200 my-1"></div>
                  
                  <button
                    onClick={() => {
                      setShowDeleteConfirm(true)
                      setShowMenu(false)
                    }}
                    className="w-full text-left px-4 py-3 hover:bg-red-50 flex items-center gap-3 rounded-b-xl transition-colors text-red-600"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    <span className="font-medium">Delete Roadmap</span>
                  </button>
                </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Save Status Message */}
        <AnimatePresence>
          {saveStatus && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className={`mb-6 rounded-2xl p-5 shadow-lg ${
                saveStatus.type === 'success'
                  ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300'
                  : 'bg-gradient-to-r from-red-50 to-pink-50 border-2 border-red-300'
              }`}
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    saveStatus.type === 'success' ? 'bg-green-500' : 'bg-red-500'
                  }`}>
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      {saveStatus.type === 'success' ? (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      ) : (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      )}
                    </svg>
                  </div>
                </div>
                <div className="flex-1">
                  <p className={`text-sm font-medium ${
                    saveStatus.type === 'success' ? 'text-green-900' : 'text-red-900'
                  }`}>
                    {saveStatus.message}
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Roadmap Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card p-8 mb-8"
        >
          <div className="flex items-start justify-between mb-6">
            <div className="flex-1">
              <h1 className="text-4xl font-display font-bold text-gray-900 mb-3">
                {currentRoadmap.title}
              </h1>
              <p className="text-lg text-gray-600 mb-4">
                {currentRoadmap.topic}
              </p>
              <div className="flex flex-wrap gap-3">
                <span className="px-4 py-2 bg-violet-100 text-violet-700 rounded-full text-sm font-medium">
                  {currentRoadmap.duration_weeks} weeks
                </span>
                <span className="px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                  {currentRoadmap.confidence ? `${Math.round(currentRoadmap.confidence * 100)}% confidence` : 'AI Generated'}
                </span>
              </div>
            </div>
          </div>

          {/* Why This Fits */}
          {currentRoadmap.why_this_fits && (
            <div className="p-4 bg-gradient-to-r from-violet-50 to-blue-50 rounded-xl border border-violet-100">
              <p className="text-sm font-medium text-violet-900 mb-1">Why this roadmap fits you:</p>
              <p className="text-gray-700">{currentRoadmap.why_this_fits}</p>
            </div>
          )}

          {/* Progress Bar */}
          <div className="mt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Progress</span>
              <span className="text-sm font-bold text-violet-600">{progress.percentage}%</span>
            </div>
            <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-violet-600 to-cyan-500"
                initial={{ width: 0 }}
                animate={{ width: `${progress.percentage}%` }}
                transition={{ duration: 0.5 }}
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {progress.completed} of {progress.total} tasks completed
            </p>
          </div>
        </motion.div>

        {/* Weekly Breakdown */}
        <div className="space-y-6">
          {currentRoadmap.weeks?.map((week, idx) => {
            const weekNumber = week.week_number || week.week || idx + 1
            const isExpanded = expandedWeeks.includes(weekNumber)
            
            return (
              <motion.div
                key={weekNumber}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
                className="relative"
              >
                {/* Week Card with glassmorphism effect */}
                <div className="card overflow-hidden border-2 border-gray-100 hover:border-violet-200 transition-all shadow-lg">
                  {/* Week Header */}
                  <button
                    onClick={() => toggleWeek(weekNumber)}
                    className="w-full p-6 flex items-center justify-between hover:bg-gradient-to-r hover:from-violet-50 hover:to-cyan-50 transition-all group"
                  >
                    <div className="flex items-center gap-5">
                      {/* Week Number Badge */}
                      <div className="relative">
                        <div className="w-16 h-16 bg-gradient-to-br from-violet-500 via-purple-500 to-cyan-500 rounded-2xl flex items-center justify-center shadow-xl transform group-hover:scale-110 transition-transform">
                          <span className="text-white font-black text-2xl">{weekNumber}</span>
                        </div>
                        {/* Glow effect */}
                        <div className="absolute inset-0 bg-gradient-to-br from-violet-400 to-cyan-400 rounded-2xl blur-lg opacity-50 group-hover:opacity-75 transition-opacity -z-10" />
                      </div>
                      
                      <div className="text-left">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-2xl font-black text-gray-900 group-hover:text-violet-700 transition-colors">
                            Week {weekNumber}
                          </h3>
                          <span className="px-3 py-1 bg-violet-100 text-violet-700 rounded-full text-xs font-bold">
                            {week.tasks?.length || 0} {week.tasks?.length === 1 ? 'Task' : 'Tasks'}
                          </span>
                        </div>
                        {week.theme && (
                          <p className="text-base font-semibold text-gray-700 group-hover:text-violet-600 transition-colors">
                            {week.theme}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    {/* Expand/Collapse Icon */}
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-medium text-gray-500 group-hover:text-violet-600 transition-colors">
                        {isExpanded ? 'Collapse' : 'Expand'}
                      </span>
                      <div className={`w-10 h-10 rounded-xl bg-gray-100 group-hover:bg-violet-100 flex items-center justify-center transition-all ${isExpanded ? 'rotate-180' : ''}`}>
                        <svg
                          className="w-5 h-5 text-gray-600 group-hover:text-violet-600 transition-colors"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>
                  </button>

                  {/* Week Tasks - Animated collapse */}
                  <motion.div
                    initial={false}
                    animate={{
                      height: isExpanded ? 'auto' : 0,
                      opacity: isExpanded ? 1 : 0
                    }}
                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                    className="overflow-hidden"
                  >
                    <div className="px-6 pb-6 space-y-4 bg-gradient-to-b from-gray-50 to-white">
                      {week.tasks?.map((task, taskIdx) => (
                        <div
                          key={task.id}
                          className="p-5 bg-white rounded-xl border-2 border-gray-100 hover:border-violet-200 transition-all shadow-sm hover:shadow-md"
                        >
                      <div className="flex items-start gap-4">
                        <button
                          onClick={() => toggleTaskComplete(task.id)}
                          className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-1 transition-colors ${
                            completedTasks.includes(task.id)
                              ? 'bg-violet-600 border-violet-600'
                              : 'border-gray-300 hover:border-violet-400'
                          }`}
                        >
                          {completedTasks.includes(task.id) && (
                            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </button>

                        <div className="flex-1">
                          <h4 className={`text-lg font-bold mb-2 ${completedTasks.includes(task.id) ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
                            {task.title}
                          </h4>
                          
                          {task.description && (
                            <p className="text-gray-600 mb-3">{task.description}</p>
                          )}

                          <div className="flex flex-wrap gap-3 mb-4">
                            <span className="text-sm text-gray-600 flex items-center gap-1">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              {task.est_hours}h
                            </span>
                            {task.deliverable && (
                              <span className="text-sm text-gray-600 flex items-center gap-1">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                {task.deliverable}
                              </span>
                            )}
                          </div>

                          {/* Resources */}
                          {task.resources && task.resources.length > 0 && (
                            <div className="space-y-2">
                              <p className="text-sm font-semibold text-gray-700 mb-2">Resources:</p>
                              {task.resources.map((resource, resIdx) => (
                                <ResourceCard key={resIdx} resource={resource} />
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            )
          })}
        </div>
      </main>

      {/* Floating Chat Widget */}
      <ChatWidget />

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {showDeleteConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowDeleteConfirm(false)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl"
            >
              <div className="text-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-black text-gray-900 mb-2">Delete Roadmap?</h3>
                <p className="text-gray-600 mb-6">
                  This action cannot be undone. Your roadmap will be permanently deleted from the database.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowDeleteConfirm(false)}
                    className="flex-1 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold rounded-xl transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDelete}
                    className="flex-1 px-6 py-3 bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700 text-white font-bold rounded-xl transition-all shadow-lg"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Publish Modal */}
      <AnimatePresence>
        {showPublishModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowPublishModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl p-8 max-w-lg w-full shadow-2xl max-h-[90vh] overflow-y-auto"
            >
              <div className="mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-violet-500 to-cyan-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-black text-gray-900 text-center mb-2">Publish to Exchange</h3>
                <p className="text-gray-600 text-center">
                  Share your roadmap with the community
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Your Name (Author)
                  </label>
                  <input
                    type="text"
                    value={publishData.author_name}
                    onChange={(e) => setPublishData({ ...publishData, author_name: e.target.value })}
                    placeholder="Enter your name"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-violet-500 focus:ring-4 focus:ring-violet-100 transition-all"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={publishData.description}
                    onChange={(e) => setPublishData({ ...publishData, description: e.target.value })}
                    placeholder="Describe your roadmap..."
                    rows={4}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-violet-500 focus:ring-4 focus:ring-violet-100 transition-all resize-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Tags (comma separated)
                  </label>
                  <input
                    type="text"
                    value={publishData.tags.join(', ')}
                    onChange={(e) => setPublishData({ ...publishData, tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean) })}
                    placeholder="Python, Web Development, Beginner"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-violet-500 focus:ring-4 focus:ring-violet-100 transition-all"
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowPublishModal(false)}
                  className="flex-1 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold rounded-xl transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handlePublish}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-violet-600 to-cyan-600 hover:from-violet-700 hover:to-cyan-700 text-white font-bold rounded-xl transition-all shadow-lg"
                >
                  Publish
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default RoadmapViewer
